import * as React from "react"
import type { TooltipContentProps } from "@radix-ui/react-tooltip"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Toggle } from "@/components/ui/toggle"
import { cn } from "@/lib/utils"
import { ScopeWrapper } from "@/components/ui/scope-wrapper"

interface ToolbarButtonProps
  extends React.ComponentPropsWithoutRef<typeof Toggle> {
  isActive?: boolean
  tooltip?: React.ReactNode
  tooltipOptions?: TooltipContentProps
}

/**
 * Toolbar control styled like the app's ghost toolbar buttons (creators
 * table "View" / "Clear"): h-7.5, dim icon that brightens on hover, muted
 * hover wash, stronger wash while pressed or while its menu is open.
 *
 * The Toggle is always controlled (`pressed` mirrors `isActive`), otherwise
 * Radix toggles its own state on click and a button looks pressed long
 * after the caret left the formatted text.
 */
export const ToolbarButton = React.forwardRef<
  HTMLButtonElement,
  ToolbarButtonProps
>(
  (
    { isActive, children, tooltip, className, tooltipOptions, pressed, ...props },
    ref
  ) => {
    const toggleButton = (
      <ScopeWrapper>
        <Toggle
          size="sm"
          ref={ref}
          pressed={pressed ?? isActive ?? false}
          className={cn(
            "h-7.5 min-w-7.5 gap-1 rounded-md px-1.5 text-xs font-medium text-foreground/80 cursor-pointer transition-colors",
            "hover:bg-muted-foreground/10 hover:text-foreground",
            "[&_svg]:size-4.5",
            "data-[state=on]:bg-foreground/10 data-[state=on]:text-foreground data-[state=on]:[&_svg]:opacity-100",
            "data-[state=open]:bg-muted-foreground/10 data-[state=open]:text-foreground data-[state=open]:[&_svg]:opacity-100",
            "disabled:opacity-40",
            className
          )}
          {...props}
        >
          {children}
        </Toggle>
      </ScopeWrapper>
    )

    if (!tooltip) {
      return toggleButton
    }

    return (
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>{toggleButton}</TooltipTrigger>
        <TooltipContent {...tooltipOptions}>
          <div className="flex items-center gap-1.5">{tooltip}</div>
        </TooltipContent>
      </Tooltip>
    )
  }
)

ToolbarButton.displayName = "ToolbarButton"

export default ToolbarButton
